import axios, { AxiosError } from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

// VITE_API_URL absolute base (e.g. https://api.rivalprop.com) for cross-origin prod;
// dev uses Vite proxy via relative '/api/v1'.
const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '') + '/api/v1'

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

// ── CSRF integration ────────────────────────────────────────────────────────
// Backend uses double-submit-cookie CSRF: GET /auth/csrf-token returns a token,
// every mutating request must echo it in `x-csrf-token`. We cache it and
// auto-refresh on 403 CSRF_FAILED. Exempt URLs (login/register/webhooks) don't
// need the header, so the token-fetch is best-effort.
let csrfToken: string | null = null

async function ensureCsrfToken(): Promise<string | null> {
  if (csrfToken) return csrfToken
  try {
    const r = await axios.get(`${API_BASE}/auth/csrf-token`, { withCredentials: true })
    csrfToken = (r.data?.data?.token as string) ?? null
  } catch {
    csrfToken = null
  }
  return csrfToken
}

api.interceptors.request.use(async (cfg) => {
  const m = (cfg.method ?? 'get').toLowerCase()
  if (m === 'post' || m === 'put' || m === 'patch' || m === 'delete') {
    const token = await ensureCsrfToken()
    if (token) {
      cfg.headers = cfg.headers ?? {}
      ;(cfg.headers as Record<string, string>)['x-csrf-token'] = token
    }
  }
  return cfg
})

api.interceptors.response.use(
  (res) => res,
  async (err: unknown) => {
    if (axios.isAxiosError(err) && err.response?.status === 403) {
      const code = (err.response.data as { error?: { code?: string } } | undefined)?.error?.code
      if (code === 'CSRF_FAILED') {
        csrfToken = null
        // Don't retry — let the caller decide. Forcing a retry can mask real bugs.
      }
    }
    return Promise.reject(err)
  },
)

// ---------- Domain types (lightweight, mirror API responses) ----------

export type Phase = 'p1' | 'p2' | 'p3' | 'p4' | 'closed'

export interface MeResponse {
  user: {
    id: string
    email: string
    displayName: string
    role: string
    countryCode: string | null
    emailVerifiedAt: string | null
    locale: string | null
  }
  account: AccountSummary | null
}

export interface AccountSummary {
  id: string
  tierCode: string
  currentPhase: Phase
  startingBalance: string
  currentBalance: string
  startedAt: string | null
  closedAt: string | null
  closeReason: string | null
}

export interface PhaseStatus {
  phase: Phase
  balance: string
  startingBalance: string
  profitTargetPct: string
  profitTargetEur: string
  profitToDateEur: string
  profitProgressPct: number
  drawdownFloor: string
  dailyLossLimit: string
  todayPnl: string
  betsPlaced: number
  betsRequired: number
  betsPending: number
  daysRemaining: number | null
  totalDays: number | null
  marketDiversity?: { spread: number; required: number; ok: boolean } | null
  passEligible: boolean
  reasons: string[]
  /** Challenge rule: min decimal odds per leg (e.g. "1.50"). */
  minOdds: string
  /** Challenge rule: max decimal odds per leg (e.g. "3.00"). */
  maxOdds: string
  /** Challenge rule: max stake as % of starting balance (e.g. 0.02 for 2%). */
  maxStakePct: number
}

export interface DailySnapshot {
  date: string
  endingBalance: string
  pnl: string
  betsPlaced: number
}

export interface LiveScore {
  home: number
  away: number
  minute?: number
  period?: string
}

export interface MarketEvent {
  id: string
  sport: string
  league: string
  leagueLogo?: string
  home: string
  homeLogo?: string
  away: string
  awayLogo?: string
  startsAt: string
  status: string
  liveScore?: LiveScore
}

export interface Market {
  id: string
  type: string
  selections: Array<{ id: string; name: string; oddsDecimal: string }>
}

export interface Bet {
  id: string
  eventId: string
  marketId: string
  selectionId: string
  selectionName: string
  oddsDecimal: string
  stake: string
  status: 'pending' | 'won' | 'lost' | 'void' | 'cancelled' | 'cashed_out'
  pnl: string
  placedAt: string
  settledAt: string | null
  eventLabel?: string | null
  sport?: string | null
  /** Closing Line Value as a decimal fraction, e.g. 0.083 for +8.3%. Null until settled. */
  clvPct: number | null
}

export interface PlaceBetInput {
  eventId: string
  marketId: string
  selectionId: string
  oddsDecimal: string
  stake: string
  /** When 2+ legs are provided, the bet is placed as a parlay. */
  legs?: Array<{ eventId: string; marketId: string; selectionId: string; oddsDecimal: string }>
}

export interface TraderScore {
  composite: number
  components: {
    discipline: number
    edge: number
    consistency: number
    riskMgmt: number
    diversity: number
  }
  updatedAt: string
}

export interface FundedCycle {
  id: string
  cycleNumber: number
  startedAt: string
  endsAt: string | null
  startingBalance: string
  currentBalance: string
  pnl: string
  status: 'active' | 'closed' | 'paid_out'
  expectedPayout: string
}

export interface Payout {
  id: string
  cycleId: string
  amount: string
  status: string
  destination: string
  requestedAt: string
  paidAt: string | null
}

export interface Tier {
  code: string
  name: string
  fee: string
  startingBalance: string
  profitTargetP1Pct: string
  profitTargetP2Pct: string
  profitSplitPct: string
  description: string | null
  // Fields surfaced by the backend's TierConfigPublic projection. Optional in
  // the type so older shapes keep type-checking, but all are present on /trader/tiers.
  feeEur?: string
  resetFeeEur?: string
  accountSizeEur?: string
  p1TargetPct?: number
  p1MaxDays?: number
  p1MinBetCount?: number
  p2TargetPct?: number
  p2MaxDays?: number
  p2MinBetCount?: number
  maxOverallDdPct?: number
  dailyLossLimitPct?: number
  maxStakePct?: number
  minOdds?: string
  maxOdds?: string
  p4ProfitSplitTrader?: number
  p4MaxAllocationEur?: string
  inactivityDaysP1?: number
  inactivityDaysP2?: number
  p1MaxBetsPerDay?: number
}

export interface ApiEnvelope<T> {
  data: T
}

// ---------- Helpers ----------

function unwrap<T>(payload: unknown): T {
  if (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)) {
    return (payload as { data: T }).data
  }
  return payload as T
}

/**
 * Unwrap a list response that may come back as either the bare `T[]` or
 * `{ <key>: T[] }`. Many backend list endpoints wrap arrays — this normalises.
 */
function unwrapList<T>(payload: unknown, key: string): T[] {
  const data = unwrap<unknown>(payload)
  if (Array.isArray(data)) return data as T[]
  if (data && typeof data === 'object' && key in (data as Record<string, unknown>)) {
    const arr = (data as Record<string, unknown>)[key]
    return Array.isArray(arr) ? (arr as T[]) : []
  }
  return []
}

export function isAxiosError(err: unknown): err is AxiosError {
  return axios.isAxiosError(err)
}

export function apiErrorMessage(err: unknown): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { error?: { message?: string }; message?: string } | undefined
    return data?.error?.message ?? data?.message ?? err.message
  }
  return err instanceof Error ? err.message : String(err)
}

// ---------- Auth ----------

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: async (): Promise<MeResponse | null> => {
      try {
        const r = await api.get('/auth/me')
        return unwrap<MeResponse>(r.data)
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 401) return null
        throw err
      }
    },
    staleTime: 30_000,
  })
}

export function useLogin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { email: string; password: string }) => {
      const r = await api.post('/auth/login', input)
      return unwrap<MeResponse>(r.data)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

export function useRegister() {
  return useMutation({
    mutationFn: async (input: {
      email: string
      password: string
      displayName: string
      countryCode: string
      acceptedTerms: boolean
    }) => {
      const r = await api.post('/auth/register', input)
      return unwrap(r.data)
    },
  })
}

export function useLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await api.post('/auth/logout')
    },
    onSuccess: () => {
      qc.clear()
    },
  })
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: async (input: { token: string }) => {
      const r = await api.post('/auth/verify-email', input)
      return unwrap(r.data)
    },
  })
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: async (input: { email: string }) => {
      const r = await api.post('/auth/forgot-password', input)
      return unwrap(r.data)
    },
  })
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async (input: { token: string; password: string }) => {
      const r = await api.post('/auth/reset-password', input)
      return unwrap(r.data)
    },
  })
}

// ---------- Profile / KYC ----------

export function useUpdateProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: Partial<{ displayName: string; locale: string; timezone: string }>) => {
      const r = await api.patch('/trader/profile', input)
      return unwrap(r.data)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

export function useKyc() {
  return useQuery({
    queryKey: ['kyc'],
    queryFn: async () => {
      try {
        const r = await api.get('/trader/profile')
        return unwrap<{ kycStatus?: string; kycCheckedAt?: string | null }>(r.data)
      } catch {
        return { kycStatus: 'not_started' as string }
      }
    },
  })
}

export function useInitKyc() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const r = await api.post('/trader/profile/kyc/init', {})
      return unwrap(r.data)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kyc'] })
    },
  })
}

// ---------- Tiers / Checkout ----------

export function useTiers() {
  return useQuery({
    queryKey: ['tiers'],
    queryFn: async () => {
      const r = await api.get('/trader/tiers')
      return unwrapList<Tier>(r.data, 'tiers')
    },
    staleTime: 5 * 60_000,
  })
}

export function useCheckout() {
  return useMutation({
    mutationFn: async (input: { tierCode: string; isReset?: boolean }) => {
      const r = await api.post('/trader/checkout', input)
      return unwrap<{ sessionId: string; checkoutUrl: string }>(r.data)
    },
  })
}

// ---------- Account / phase ----------

export function useAccount() {
  return useQuery({
    queryKey: ['account'],
    queryFn: async () => {
      const r = await api.get('/trader/account')
      return unwrap<AccountSummary>(r.data)
    },
  })
}

/** Zero state for a user with no active challenge yet. */
const EMPTY_PHASE_STATUS: PhaseStatus = {
  phase:              'p1',
  balance:            '0',
  startingBalance:    '0',
  profitTargetPct:    '0',
  profitTargetEur:    '0',
  profitToDateEur:    '0',
  profitProgressPct:  0,
  drawdownFloor:      '0',
  dailyLossLimit:     '0',
  todayPnl:           '0',
  betsPlaced:         0,
  betsRequired:       0,
  betsPending:        0,
  daysRemaining:      null,
  totalDays:          null,
  marketDiversity:    null,
  passEligible:       false,
  reasons:            [],
  minOdds:            '1.50',
  maxOdds:            '3.00',
  maxStakePct:        0.02,
}

export function usePhaseStatus() {
  return useQuery({
    queryKey: ['phase-status'],
    queryFn: async (): Promise<PhaseStatus> => {
      let r
      try {
        r = await api.get('/trader/account/phase-status')
      } catch (err) {
        // No active account yet — return zero state so the dashboard still renders.
        if (isAxiosError(err) && (err.response?.status === 404 || err.response?.status === 409)) {
          return EMPTY_PHASE_STATUS
        }
        throw err
      }
      // Backend returns a nested payload (tier/balance/profitTarget/drawdown/dailyLoss/
      // timeRemaining/betCount objects). Flatten to the legacy PhaseStatus shape the
      // UI was originally built against.
      const raw = unwrap<{
        accountId: string
        currentPhase: Phase
        status: string
        tier: {
          p1TargetPct: number
          p2TargetPct: number
          minOdds?: string
          maxOdds?: string
          maxStakePct?: number
        }
        balance: { currentEur: string; startingEur: string; pnlEur: string }
        profitTarget: { targetEur: string; remainingEur: string; progressPct: number }
        drawdown: { floorEur: string; maxAllowedPct: number }
        dailyLoss: { todayPnlEur: string; limitEur: string }
        timeRemaining: { daysRemaining: number | null; totalDays?: number }
        betCount: { settled: number; required: number; remaining?: number; pending?: number }
      }>(r.data)
      const targetPct = raw.currentPhase === 'p2' ? raw.tier.p2TargetPct : raw.tier.p1TargetPct
      return {
        phase:              raw.currentPhase,
        balance:            raw.balance.currentEur,
        startingBalance:    raw.balance.startingEur,
        profitTargetPct:    String((targetPct ?? 0) * 100),
        profitTargetEur:    raw.profitTarget.targetEur,
        profitToDateEur:    raw.balance.pnlEur,
        profitProgressPct:  raw.profitTarget.progressPct,
        drawdownFloor:      raw.drawdown.floorEur,
        dailyLossLimit:     raw.dailyLoss.limitEur,
        todayPnl:           raw.dailyLoss.todayPnlEur,
        betsPlaced:         raw.betCount.settled,
        betsRequired:       raw.betCount.required,
        betsPending:        raw.betCount.pending ?? 0,
        daysRemaining:      raw.timeRemaining.daysRemaining,
        totalDays:          raw.timeRemaining.totalDays ?? null,
        marketDiversity:    null,
        passEligible:       false,
        reasons:            [],
        minOdds:            raw.tier.minOdds ?? '1.50',
        maxOdds:            raw.tier.maxOdds ?? '3.00',
        maxStakePct:        raw.tier.maxStakePct ?? 0.02,
      }
    },
    refetchInterval: 30_000,
  })
}

export function useDailySnapshots(days = 30) {
  return useQuery({
    queryKey: ['daily-snapshots', days],
    queryFn: async (): Promise<DailySnapshot[]> => {
      const r = await api.get('/trader/account/daily-snapshots', { params: { days } })
      // Backend may wrap as { snapshots: [...] } or return the array directly.
      const raw = unwrap<DailySnapshot[] | { snapshots: DailySnapshot[] }>(r.data)
      if (Array.isArray(raw)) return raw
      return raw?.snapshots ?? []
    },
  })
}

// ---------- Events / Bets ----------

/**
 * Sports we ask API-SPORTS to surface. Backend defaults to football alone if
 * sport is not provided — we explicitly fan out so the sportsbook shows
 * basketball, baseball, hockey, rugby, volleyball, handball alongside it.
 */
const FALLBACK_SPORTS = ['football', 'basketball', 'baseball', 'hockey', 'rugby', 'volleyball', 'handball'] as const

export function useAllowedSports() {
  return useQuery({
    queryKey: ['tier-allowed-sports'],
    queryFn: async (): Promise<string[]> => {
      const r = await api.get('/trader/tiers/me/sports')
      return unwrapList<string>(r.data, 'sports')
    },
    staleTime: 5 * 60_000,
  })
}

type RawEvent = {
  externalId?: string
  id?: string
  sport: string
  league: string
  leagueLogo?: string
  homeTeam?: string
  awayTeam?: string
  home?: string
  away?: string
  homeLogo?: string
  awayLogo?: string
  startAt?: string
  startsAt?: string
  status: string
  liveScore?: LiveScore
}

function mapEvent(e: RawEvent): MarketEvent {
  return {
    id:         e.id ?? e.externalId ?? '',
    sport:      e.sport,
    league:     e.league,
    leagueLogo: e.leagueLogo,
    home:       e.home ?? e.homeTeam ?? '',
    homeLogo:   e.homeLogo,
    away:       e.away ?? e.awayTeam ?? '',
    awayLogo:   e.awayLogo,
    startsAt:   e.startsAt ?? e.startAt ?? '',
    status:     e.status,
    liveScore:  e.liveScore,
  }
}

export function useEvents(params?: { sport?: string; status?: string; allowedSports?: readonly string[] }) {
  return useQuery({
    queryKey: ['events', params],
    queryFn: async (): Promise<MarketEvent[]> => {
      // If a specific sport was requested, hit just that.
      if (params?.sport) {
        const { sport, status } = params
        const r = await api.get('/trader/events', { params: { sport, status } })
        return unwrapList<RawEvent>(r.data, 'events').map(mapEvent)
      }
      // No sport filter: fan out across the tier-allowed sports in parallel
      // and merge. Errors per-sport are swallowed so the page still renders.
      const sports = (params?.allowedSports && params.allowedSports.length > 0)
        ? params.allowedSports
        : FALLBACK_SPORTS
      const results = await Promise.all(
        sports.map(async (sport) => {
          try {
            const r = await api.get('/trader/events', { params: { sport, status: params?.status } })
            return unwrapList<RawEvent>(r.data, 'events').map(mapEvent)
          } catch {
            return [] as MarketEvent[]
          }
        }),
      )
      // De-duplicate by event id and sort by start time ascending.
      const seen = new Set<string>()
      const merged: MarketEvent[] = []
      for (const arr of results) for (const e of arr) {
        if (!e.id || seen.has(e.id)) continue
        seen.add(e.id)
        merged.push(e)
      }
      merged.sort((a, b) => (a.startsAt || '').localeCompare(b.startsAt || ''))
      return merged
    },
    staleTime: 30_000,
    // Re-poll while any event is live so scores tick. 30s matches api-sports
    // free-tier rate limits comfortably; tighten only if upgrading the plan.
    refetchInterval: (q) => {
      const data = q.state.data as MarketEvent[] | undefined
      return data?.some((e) => e.status === 'live') ? 30_000 : false
    },
  })
}

export function useEventMarkets(eventId: string | null) {
  return useQuery({
    queryKey: ['event-markets', eventId],
    enabled: !!eventId,
    queryFn: async (): Promise<Market[]> => {
      const r = await api.get(`/trader/events/${eventId}/markets`)
      // Backend returns a flat list of selections: [{ externalId, marketType, selection, selectionLabel, odds }].
      // Group by marketType into the nested Market shape the UI consumes.
      const flat = unwrapList<{
        externalId: string
        marketType: string
        selection: string
        selectionLabel: string
        odds: string
      }>(r.data, 'markets')
      const byType = new Map<string, Market>()
      for (const row of flat) {
        let m = byType.get(row.marketType)
        if (!m) {
          m = { id: row.marketType, type: row.marketType, selections: [] }
          byType.set(row.marketType, m)
        }
        m.selections.push({
          id:          row.externalId,
          name:        row.selectionLabel,
          oddsDecimal: row.odds,
        })
      }
      return Array.from(byType.values())
    },
  })
}

type RawBet = {
  id: string
  externalEventId?: string
  eventId?: string
  marketType?: string
  marketId?: string
  selection?: string
  selectionId?: string
  selectionLabel?: string
  selectionName?: string
  oddsTaken?: string
  oddsDecimal?: string
  stakeEur?: string
  stake?: string
  status: Bet['status']
  settlementAmountEur?: string | null
  settledAt?: string | null
  placedAt?: string
  createdAt?: string
  homeTeam?: string | null
  awayTeam?: string | null
  league?: string | null
  sport?: string | null
  eventLabel?: string | null
  clvPct?: number | null
}

function mapBet(raw: RawBet): Bet {
  const home = raw.homeTeam ?? ''
  const away = raw.awayTeam ?? ''
  const fallbackId = raw.externalEventId ?? raw.eventId ?? raw.id ?? ''
  const label = raw.eventLabel
    ?? (home && away ? `${home} vs ${away}` : (fallbackId.length > 24 ? fallbackId.slice(0, 24) : fallbackId))
  const stake = Number(raw.stakeEur ?? raw.stake ?? 0)
  const settled = Number(raw.settlementAmountEur ?? 0)
  // Settled bets: pnl = payout − stake. Pending/cancelled: 0.
  const pnl = (raw.status === 'won' || raw.status === 'lost' || raw.status === 'void')
    ? (settled - stake).toFixed(2)
    : '0'
  return {
    id:            raw.id ?? '',
    eventId:       raw.externalEventId ?? raw.eventId ?? '',
    marketId:      raw.marketType ?? raw.marketId ?? '',
    selectionId:   raw.selection ?? raw.selectionId ?? '',
    selectionName: raw.selectionLabel ?? raw.selectionName ?? raw.selection ?? '',
    oddsDecimal:   raw.oddsTaken ?? raw.oddsDecimal ?? '0',
    stake:         raw.stakeEur ?? raw.stake ?? '0',
    status:        raw.status,
    pnl,
    placedAt:      raw.placedAt ?? raw.createdAt ?? '',
    settledAt:     raw.settledAt ?? null,
    eventLabel:    label,
    sport:         raw.sport ?? null,
    clvPct:        raw.clvPct ?? null,
  }
}

export function useBets(params?: { status?: string }) {
  return useQuery({
    queryKey: ['bets', params],
    queryFn: async () => {
      const r = await api.get('/trader/bets', { params })
      return unwrapList<RawBet>(r.data, 'bets').map(mapBet)
    },
  })
}

function uuidv4(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

export function usePlaceBet() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: PlaceBetInput) => {
      // Map UI shape → backend Zod shape. UI passes eventId (which IS the
      // externalEventId from the API-SPORTS adapter), marketId == marketType,
      // selectionId in the form `externalEventId:marketType:selection` —
      // strip back to selection code if needed.
      const stripSel = (s: string) => (s.includes(':') ? (s.split(':').pop() ?? s) : s)
      const selection = stripSel(input.selectionId)
      const body: Record<string, unknown> = {
        externalEventId: input.eventId,
        marketType:      input.marketId,
        selection,
        oddsTaken:       Number(input.oddsDecimal).toFixed(2),
        stakeEur:        Number(input.stake).toFixed(2),
        requestId:       uuidv4(),
      }
      if (input.legs && input.legs.length >= 2) {
        body.legs = input.legs.map((l) => ({
          externalEventId: l.eventId,
          marketType:      l.marketId,
          selection:       stripSel(l.selectionId),
          oddsTaken:       Number(l.oddsDecimal).toFixed(2),
        }))
      }
      const r = await api.post('/trader/bets', body)
      return unwrap<Bet>(r.data)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['phase-status'] })
      void qc.invalidateQueries({ queryKey: ['bets'] })
      void qc.invalidateQueries({ queryKey: ['account'] })
    },
  })
}

export function useCancelBet() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (betId: string) => {
      const r = await api.delete(`/trader/bets/${betId}`)
      return unwrap(r.data)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bets'] })
      void qc.invalidateQueries({ queryKey: ['phase-status'] })
    },
  })
}

// ---------- Score ----------

export function useScore() {
  return useQuery({
    queryKey: ['score'],
    queryFn: async (): Promise<TraderScore | null> => {
      try {
        const r = await api.get('/trader/score')
        return unwrap<TraderScore>(r.data)
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) return null
        throw err
      }
    },
  })
}

export function useScoreHistory(days = 30) {
  return useQuery({
    queryKey: ['score-history', days],
    queryFn: async () => {
      try {
        const r = await api.get('/trader/score/history', { params: { days } })
        return unwrapList<{ date: string; composite: number }>(r.data, 'history')
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) return []
        throw err
      }
    },
  })
}

// ---------- Funded ----------

export function useFundedCycle() {
  return useQuery({
    queryKey: ['funded-cycle'],
    queryFn: async (): Promise<FundedCycle | null> => {
      try {
        const r = await api.get('/trader/funded/cycle')
        return unwrap<FundedCycle>(r.data)
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) return null
        throw err
      }
    },
  })
}

export function useFundedCycles() {
  return useQuery({
    queryKey: ['funded-cycles'],
    queryFn: async () => {
      try {
        const r = await api.get('/trader/funded/cycles')
        return unwrapList<FundedCycle>(r.data, 'cycles')
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) return []
        throw err
      }
    },
  })
}

export function usePayouts() {
  return useQuery({
    queryKey: ['payouts'],
    queryFn: async () => {
      const r = await api.get('/trader/funded/payouts')
      return unwrapList<Payout>(r.data, 'payouts')
    },
  })
}

export function useRequestPayout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { method: string; destination: Record<string, string> }) => {
      const r = await api.post('/trader/funded/payout-request', input)
      return unwrap<Payout>(r.data)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payouts'] })
      void qc.invalidateQueries({ queryKey: ['funded-cycle'] })
    },
  })
}

// ---------- Leaderboard ----------

export interface LeaderboardRow {
  rank:        number
  accountId:   string
  userId:      string
  displayName: string
  tierCode:    string
  score:       number
  pnlEur:      string
  self:        boolean
}

export function useLeaderboard() {
  return useQuery({
    queryKey: ['leaderboard'],
    queryFn: async (): Promise<{ rows: LeaderboardRow[]; selfRank: number | null }> => {
      const r = await api.get('/trader/leaderboard')
      return unwrap<{ rows: LeaderboardRow[]; selfRank: number | null }>(r.data)
    },
    staleTime: 60_000,
  })
}

// ---------- Notifications (placeholder) ----------

export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      try {
        const r = await api.get('/trader/notifications')
        return unwrapList<{ id: string; title: string; body: string; createdAt: string; readAt: string | null }>(r.data, 'notifications')
      } catch {
        return []
      }
    },
    staleTime: 30_000,
  })
}
